import { useEffect, useRef, useState } from "react";
import { useAgentSession } from "./sessions";
import { getAgentChatContext } from "./chat.context";
import { useMadState } from "../state";
/**
 * Hook for managing a chat session with an AI agent.
 * Provides functionality to send messages to the agent and tracks the status of the session and messages.
 * Agent responses are streamed incrementally via SSE.
 * @param agent The name of the AI agent. {@link AgentName}
 * @param sessionId Optional session ID (or proxy ID — e.g., "chat:general") to use for the chat.
 * @param options Optional configuration for the chat behavior. {@link UseAgentChatOptions}
 */
export function useAgentChat(agent, sessionId, { context, contextKey = sessionId, ensureSession = true, storage, ttl, onEventData, lazy = false, onNewSession } = {}) {
    const [isActivated, setIsActivated] = useState(!lazy);
    const { isLoading, isInitializing, isLoaded, error, isSaving, data: session, pendingChanges, isCreatingSession, sessionObserver, reload, change, save, withEnsuredSession, newSession } = useAgentSession(agent, isActivated ? sessionId : undefined, {
        ensureSession,
        storage,
        ttl
    });
    // Abort controller for managing SSE streaming cancellation
    const [abortController, setAbortController] = useMadState(`${agent}:${sessionId}:abortController`);
    // Tracks the latest abortController so the unmount cleanup below (registered once, with a stale closure) can still reach the current value.
    const abortControllerRef = useRef(abortController);
    abortControllerRef.current = abortController;
    const streamingMessageIdRef = useRef(null);
    const ephemeralUserMessageIdRef = useRef(null);
    const pendingMessageRef = useRef(null);
    const activateResolverRef = useRef(null);
    const hasNotifiedNewSessionRef = useRef(false);
    const messages = pendingChanges?.events || [];
    let status = "ready";
    if (!isActivated) {
        status = "idle";
    }
    else {
        if (error) {
            status = "error";
        }
        if (isLoading ||
            isSaving ||
            isInitializing ||
            (!isLoaded && !error) ||
            pendingMessageRef.current) {
            status = "processing";
        }
    }
    const onEventDataRef = useRef(onEventData);
    onEventDataRef.current = onEventData;
    /**
     * Sends a message to the agent within the current session.
     * The message will be sent via SSE and the response will be streamed incrementally.
     * When `ensureSession` is enabled, the function will ensure that a valid session exists before sending the message,
     * and will handle session creation if needed.
     * In lazy mode, the first call queues the message and activates the session — it will be sent once the session is ready.
     * @param message the text content of the message
     * @throws Will throw an error if the request fails for reasons other than an abort (cancellation).
     */
    async function sendMessage(message, { attachments } = {}) {
        // If not yet activated (lazy mode), queue the message and activate
        if (!isActivated) {
            // Build ephemeral events locally for immediate display — don't call change()
            // on the undefined-session observer since it will be discarded on activation.
            const events = [
                {
                    content: { parts: [{ text: message }] },
                    attachments,
                    role: "user",
                    author: "user",
                    confidence: 0,
                    id: crypto.randomUUID(),
                    timestamp: Date.now()
                },
                {
                    content: { parts: [] },
                    role: "model",
                    author: "model",
                    confidence: 0,
                    id: crypto.randomUUID(),
                    timestamp: Date.now() + 1
                }
            ];
            pendingMessageRef.current = { message, attachments, events };
            setIsActivated(true);
            return;
        }
        if (isSaving) {
            // Prevent sending a new message if one is already being sent
            console.warn("A message is already being sent. Please wait for the current message to resolve or abort it before sending a new message.");
            return;
        }
        await handleSend(message, { attachments });
    }
    /**
     * Internal send logic — shared by both normal sendMessage and the lazy activation effect.
     */
    async function handleSend(message, { attachments } = {}) {
        setEphemeralMessages(message, { attachments });
        const nextAbortController = new AbortController();
        // Create abort controller for cancellation
        setAbortController(nextAbortController);
        const sendRequest = async (id) => {
            // Read without subscribing, so context changes don't re-render the chat or outlive their writers.
            const mergedContext = {
                ...context,
                ...getAgentChatContext(agent, contextKey),
                ...(attachments?.length ? { attachments } : {})
            };
            const requestContext = Object.keys(mergedContext).length ? mergedContext : undefined;
            // SSE callbacks for handling streaming responses from the agent.
            const sse = {
                onEvent(event) {
                    updateStreamingSessionEvent(event);
                    if (onEventData && typeof event?.data !== "undefined") {
                        onEventDataRef.current?.(event);
                    }
                },
                onError(event) {
                    updateStreamingSessionError(event);
                }
            };
            try {
                return await save({
                    id,
                    message
                }, {
                    context: requestContext,
                    sse,
                    signal: nextAbortController.signal
                });
            }
            catch (err) {
                if (err instanceof DOMException && err.name === "AbortError") {
                    // Request was aborted, do not treat as error
                    return;
                }
                throw err;
            }
        };
        const onError = (err) => {
            const events = (pendingChanges?.events || [])?.filter((e) => e.id !== streamingMessageIdRef.current); // Remove streaming message placeholder if it exists
            updateSessionEvents(events.map((e) => {
                if (e.id === ephemeralUserMessageIdRef.current) {
                    return { ...e, errorMessage: err instanceof Error ? err.message : String(err) };
                }
                return e;
            }));
        };
        try {
            if (!ensureSession && !sessionId.includes(":")) {
                // If we are not ensuring session, just attempt to send the prompt with the current session ID
                // Missing ID or expired sessions will result in an error from the save call
                await sendRequest(session?.id).catch(onError);
                return;
            }
            await withEnsuredSession(sendRequest, { onError: (_, err) => onError(err) });
        }
        finally {
            resetEphemeralIds();
            setAbortController(undefined);
        }
    }
    function handleNewSessionNotification() {
        if (!session?.id || hasNotifiedNewSessionRef.current) {
            return;
        }
        hasNotifiedNewSessionRef.current = true;
        if (sessionId === session.id || !onNewSession) {
            return;
        }
        onNewSession(session.id);
    }
    /**
     * Imperatively activates the session and returns the session ID once available.
     * Use this for operations that need a session ID without sending a message (e.g., file uploads).
     * If the session is already active, returns the existing ID immediately.
     * If the sessionId is already a real ID (not a proxy), returns it directly.
     */
    async function activate() {
        // If already activated and we have a session, return immediately
        if (isActivated && session?.id) {
            return session.id;
        }
        // If sessionId is already a real ID (not a proxy), return it directly
        if (!sessionId.includes(":")) {
            setIsActivated(true);
            return sessionId;
        }
        const prev = activateResolverRef.current;
        // Otherwise, activate and wait for session to become ready
        return new Promise((resolve, reject) => {
            activateResolverRef.current = {
                resolve: (id) => {
                    if (prev) {
                        prev.resolve(id);
                    }
                    resolve(id);
                },
                reject: (error) => {
                    if (prev) {
                        prev.reject(error);
                    }
                    reject(error);
                }
            };
            setIsActivated(true);
        });
    }
    /**
     * Updates the session data with new "ephemeral" messages for the user and model,
     * which allows for immediate UI updates while awaiting responses from the agent.
     * These messages are tracked with refs so they are properly updated or removed based on session updates or if errors occur.
     */
    function setEphemeralMessages(message, { attachments } = {}) {
        let events = [...messages];
        const lastUserErrorWithSameMessage = events
            .slice(-2)
            .find((e) => e.role === "user" && e.errorMessage && partsText(e.content?.parts ?? []) === message);
        if (events[events.length - 1]?.id === streamingMessageIdRef.current &&
            lastUserErrorWithSameMessage) {
            // If the last event is the streaming placeholder and there is a user message with an error that has the same content as the new message,
            // we want to remove that user message because it will be replaced with the new one, and we don't want duplicate messages in the UI.
            events.splice(events.length - 2, 1);
        }
        else if (lastUserErrorWithSameMessage) {
            // If there is a user message with an error that has the same content as the new message, we want to remove that user message because it will be replaced with the new one, and we don't want duplicate messages in the UI.
            events = events.filter((e) => e.id !== lastUserErrorWithSameMessage.id);
        }
        ephemeralUserMessageIdRef.current = crypto.randomUUID();
        // For the new user message, we add it as an ephemeral event to the session data,
        // which allows it to show up in the UI immediately while we await the response from the agent.
        // We generate a random ID for it so that we can track and update/remove it later based on the session updates or if an error occurs.
        events.push({
            content: { parts: [{ text: message }] },
            attachments,
            role: "user",
            author: "user",
            confidence: 0,
            id: ephemeralUserMessageIdRef.current,
            timestamp: Date.now()
        });
        // Placeholder for the model response, updated incrementally as SSE events arrive.
        // We only add the placeholder when we initiate the streaming request, and we track it
        // with streamingMessageIdRef so that we can update it later based on the session updates
        // or if an error occurs.
        if (!streamingMessageIdRef.current) {
            streamingMessageIdRef.current = crypto.randomUUID();
            events.push({
                content: { parts: [] },
                role: "model",
                author: "model",
                confidence: 0,
                id: streamingMessageIdRef.current,
                timestamp: Date.now() + 1
            });
        }
        updateSessionEvents(events);
    }
    /**
     * Utility function to update session data like to deltas/loading/saving/error states,
     * while preserving existing values when not explicitly overwritten.
     */
    function updateSessionEvents(events) {
        change({ events }, false);
    }
    /**
     * Utility for handling an SSE error event, marking the ephemeral placeholder event as errored.
     */
    function updateStreamingSessionError(error) {
        updateSessionEvents((pendingChanges?.events || []).map((e) => e.id === streamingMessageIdRef.current ? { ...e, errorMessage: error.error } : e));
    }
    /**
     * Handles an incoming streamed event, merging its content into the placeholder so the UI reflects
     * the response in real time. An ADK turn emits many events — partial text deltas, one or more
     * non-partial text events, and non-text events (tool calls/results, control signals) — so we
     * cannot simply "replace on non-partial":
     *   - Partial chunks are appended to the accumulated content.
     *   - A non-partial text event may be a cumulative aggregate or a final delta, so we reconcile
     *     defensively: if its text is a superset (prefix match) of what we've accumulated, replace;
     *     otherwise append.
     *   - Non-text events (no text parts, e.g. a function call or null content) must not clobber the
     *     visible text, so they leave the accumulated content untouched.
     */
    function updateStreamingSessionEvent(event) {
        const messageEvent = pendingChanges?.events?.find((e) => e.id === streamingMessageIdRef.current);
        if (!messageEvent) {
            // If we don't have the ephemeral event in our pending changes, it means the session was likely updated and the placeholder event was removed.
            // In this case, we don't want to do anything with the incoming SSE events because we no longer have a reference point for where to apply them in our session data.
            return;
        }
        messageEvent.author = event.author;
        messageEvent.confidence = event.confidence ?? messageEvent.confidence;
        const incomingParts = event.content?.parts ?? [];
        const accumulatedParts = messageEvent.content?.parts ?? [];
        const append = () => {
            messageEvent.content = { parts: [...accumulatedParts, ...incomingParts] };
        };
        if (event.partial) {
            append();
        }
        else if (incomingParts.some((part) => typeof part.text === "string")) {
            const accumulatedText = partsText(accumulatedParts);
            const incomingText = partsText(incomingParts);
            if (accumulatedText && incomingText.startsWith(accumulatedText)) {
                // Cumulative aggregate: replace the accumulated content with the full event content.
                messageEvent.content = event.content;
            }
            else {
                append();
            }
        }
        // Otherwise this is a non-text event (tool call/result, control signal, null content) — leave
        // the accumulated visible content untouched so it isn't wiped mid-turn.
        updateSessionEvents([...(pendingChanges?.events || [])]);
    }
    /**
     * Cancels the current streaming request if one is in progress.
     */
    const abortRequest = (isCleanup = false) => {
        resetEphemeralIds();
        const controller = abortControllerRef.current;
        if (!controller) {
            return;
        }
        controller.abort();
        sessionObserver.next({
            ...sessionObserver.value,
            isSaving: false
        });
        setAbortController(undefined);
        if (isCleanup === true) {
            return;
        }
        updateSessionEvents([
            ...(pendingChanges?.events || []),
            {
                content: { parts: [{ text: "You stopped this response." }] },
                role: "model",
                author: "system",
                confidence: 0,
                id: crypto.randomUUID(),
                timestamp: Date.now()
            }
        ]);
    };
    // Clean up on unmount
    useEffect(() => {
        return () => {
            // abort any in-progress requests
            abortRequest(true);
        };
    }, []);
    function resetEphemeralIds() {
        streamingMessageIdRef.current = null;
        ephemeralUserMessageIdRef.current = null;
    }
    async function handleNewSession() {
        const newId = await newSession();
        if (newId) {
            onNewSession?.(newId);
        }
        return newId;
    }
    // Effect: once activated and session is ready, resolve pending activate() and/or send queued message.
    // If session creation fails, reject the pending activate() promise and clear the queued message.
    useEffect(() => {
        if (!isActivated) {
            return;
        }
        // Reject pending activate() on session creation failure
        if (error && activateResolverRef.current) {
            activateResolverRef.current.reject(error instanceof Error ? error : new Error(String(error)));
            activateResolverRef.current = null;
            pendingMessageRef.current = null;
            return;
        }
        if (!session?.id) {
            return;
        }
        // Resolve any pending activate() promise
        if (activateResolverRef.current) {
            activateResolverRef.current.resolve(session.id);
            activateResolverRef.current = null;
            handleNewSessionNotification();
        }
        if (isSaving || !pendingMessageRef.current) {
            return;
        }
        // Send any queued pending message (waits for isSaving to clear)
        const { message, attachments } = pendingMessageRef.current;
        pendingMessageRef.current = null;
        handleNewSessionNotification();
        handleSend(message, { attachments });
    }, [isActivated, session?.id, isSaving, error]);
    return {
        session,
        messages: pendingMessageRef.current?.events || pendingChanges?.events || [],
        error,
        status,
        isCreatingSession,
        sendMessage,
        activate,
        abort: abortController ? abortRequest : undefined,
        newSession: handleNewSession,
        reload: () => reload(true)
    };
}
/** Concatenates the text of every part, ignoring non-text parts. */
function partsText(parts) {
    return parts.map((part) => part.text || "").join("");
}
//# sourceMappingURL=chat.js.map